package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.utils.GetParashotUseCase
import com.eladfinish.bible.contest.utils.gematriaWord
import com.eladfinish.bible.contest.utils.makeVavAndYudOptional

/** Where a parasha begins and ends in its chumash; every number is 1-based. */
data class ParashaRange(
    val bookNumber: Int,
    val chapterStart: Int,
    val verseStart: Int,
    val chapterEnd: Int,
    val verseEnd: Int,
)

/**
 * Finds the verses of a parasha from the name the Hebrew calendar gives it. The name is matched the
 * same way [com.eladfinish.bible.contest.features.hebrewCalendar.GetCurrentParashaUseCase] does, so
 * a combined week ("ויקהל פקודי") resolves to the range of both parashot.
 */
class GetParashaRangeUseCase(
    private val getParashotUseCase: GetParashotUseCase,
) {

    /** `null` when [parashaName] is not a parasha of the Torah (a holiday, for instance). */
    operator fun invoke(parashaName: String): ParashaRange? {
        for (bookNumber in 1..5) {
            val parashot = getParashotUseCase.parseParashot(bookNumber = bookNumber)
            val position = parashot.parashotNames.indexOfFirst {
                parashaName.matches(it.makeVavAndYudOptional().replaceRegex(regex = " -", replacement = "").toRegex())
            }
            if (position != -1) {
                return parse(bookNumber = bookNumber, source = parashot.parashotSources[position])
            }
        }
        return null
    }

    // "א,א-ו,א": chapter and verse where it begins, chapter and verse where it ends, in Hebrew letters
    private fun parse(bookNumber: Int, source: String): ParashaRange? {
        val (start, end) = source.split("-").takeIf { it.size == 2 } ?: return null
        val (chapterStart, verseStart) = start.split(",").takeIf { it.size == 2 } ?: return null
        val (chapterEnd, verseEnd) = end.split(",").takeIf { it.size == 2 } ?: return null
        return ParashaRange(
            bookNumber = bookNumber,
            chapterStart = chapterStart.gematriaWord(),
            verseStart = verseStart.gematriaWord(),
            chapterEnd = chapterEnd.gematriaWord(),
            verseEnd = verseEnd.gematriaWord(),
        )
    }
}
